import type { Address } from "viem"
import { readContract } from "@wagmi/core"
import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { ApprovalMode, ContractRiskLevel } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenStore } from "@/domains/tokens"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { erc20Abi } from "@/domains/abis"
import { determineApprovalAmount, unixTimestamp } from "@/domains/library"
import { OrchestratedQuery } from "@/domains/library/orchestrated-query"
import type { KyberRouteResponse } from "@/domains/validators"
import { kyberBuildResponseSchema } from "@/domains/validators"

import type {
  IAggregatorApiTransport,
  IAggregatorOnChainTransport,
  IPriceImpactCalculator,
  ISwapAggregatorPlugin,
  ISwapAggregatorSettingsStore,
  ISwapCoordinator,
  ISwapInputStore,
} from "../interfaces"
import type {
  AggregatorQuoteRequest,
  AggregatorTransactionRequest,
  NormalizedSwapQuote,
  NormalizedSwapTransaction,
  SwapQuoteRequestData,
} from "../types"
import {
  SwapAggregatorDisabledEvent,
  SwapAggregatorEnabledEvent,
  SwapApprovalCompletedEvent,
  SwapApprovalStartedEvent,
  SwapErrorEvent,
  SwapQuoteReceivedEvent,
  SwapQuoteRequestClearedEvent,
  SwapQuoteRequestedEvent,
} from "../events"
import { KyberApiTransport } from "../transports/kyber-api-transport"

/**
 * Kyber Swap Plugin
 * Implements ISwapAggregatorPlugin for KyberSwap Aggregator API v1
 */
export class KyberSwapPlugin implements ISwapAggregatorPlugin {
  readonly id = "kyber"
  readonly name = "KyberSwap"
  readonly displayName = "KyberSwap"
  readonly description = "Reliable multi-source DEX aggregator with superior routing"
  readonly icon = "/brands/kyber-icon.svg"
  readonly color = "from-emerald-500 to-teal-500"
  readonly isProductionReady = true

  latestQuote: NormalizedSwapQuote | null = null
  selectedQuote: NormalizedSwapQuote | null = null

  private currentQuoteRequest: SwapQuoteRequestData | null = null
  private quoteQuery: OrchestratedQuery<void> | null = null
  private apiTransport: IAggregatorApiTransport | null = null
  private initialized = false

  private orchestratedQueryStore: OrchestratedQueryStore | null = null
  private eventBus: IEventBus | null = null
  private swapCoordinator: ISwapCoordinator | null = null
  private inputStore: ISwapInputStore | null = null
  private priceImpactCalculator: IPriceImpactCalculator | null = null
  private chainId: number | null = null
  private nativeToken: TokenStore | null = null
  private walletStore: IWalletConnectionStore | null = null
  private slippageBps: number | null = null
  private settingsStore: ISwapAggregatorSettingsStore | null = null
  private config: any = null

  constructor(
    private readonly priceImpactCalculatorParam: IPriceImpactCalculator,
    private readonly getConfig: () => any,
  ) {
    makeObservable(this, {
      latestQuote: observable,
      selectedQuote: observable,
      selectCurrentQuote: action,
      updateSelectedQuote: action,
      clearSelection: action,
      displayQuote: computed,
      hasBetterQuote: computed,
      isLoadingQuotes: computed,
    })
  }

  get isLoadingQuotes(): boolean {
    return this.quoteQuery?.isRunning ?? false
  }

  initialize(
    orchestratedQueryStore: OrchestratedQueryStore,
    eventBus: IEventBus,
    swapCoordinator: ISwapCoordinator,
    inputStore: ISwapInputStore,
    priceImpactCalculator: IPriceImpactCalculator,
    chainId: number,
    nativeToken: TokenStore,
    walletStore: IWalletConnectionStore,
    slippageBps: number,
    settingsStore: ISwapAggregatorSettingsStore,
    apiTransport?: IAggregatorApiTransport,
    _onChainTransport?: IAggregatorOnChainTransport,
  ): void {
    if (this.initialized) {
      throw new Error("Kyber plugin already initialized")
    }

    this.orchestratedQueryStore = orchestratedQueryStore
    this.eventBus = eventBus
    this.swapCoordinator = swapCoordinator
    this.inputStore = inputStore
    this.priceImpactCalculator = priceImpactCalculator
    this.chainId = chainId
    this.nativeToken = nativeToken
    this.walletStore = walletStore
    this.slippageBps = slippageBps
    this.settingsStore = settingsStore
    this.config = this.getConfig()

    this.apiTransport = apiTransport ?? new KyberApiTransport()

    const queryName = `Kyber Quotes`
    this.quoteQuery = new OrchestratedQuery<void>(
      queryName,
      (_query) => this.fetchQuoteInternal(),
      10_000,
      this.onQuoteSuccess.bind(this),
      this.onQuoteFailed.bind(this),
      orchestratedQueryStore,
    )

    swapCoordinator.registerQuoteQuery(queryName)

    eventBus.subscribe(SwapQuoteRequestedEvent.TYPE, this.handleQuoteRequested.bind(this))
    eventBus.subscribe(SwapQuoteRequestClearedEvent.TYPE, this.handleQuoteRequestCleared.bind(this))
    eventBus.subscribe(SwapAggregatorEnabledEvent.TYPE, this.handleAggregatorEnabled.bind(this))
    eventBus.subscribe(SwapAggregatorDisabledEvent.TYPE, this.handleAggregatorDisabled.bind(this))

    this.initialized = true
  }

  private isEnabled(): boolean {
    if (!this.settingsStore) return false
    const aggregator = this.settingsStore.SwapAggregators.find(this.id)
    if (this.settingsStore.SwapAggregators.fallbackMode === "multi") {
      return Boolean(aggregator?.enabled)
    }
    return aggregator?.enabled || this.settingsStore.SwapAggregators.activeFallbackAggregatorId === this.id || false
  }

  private async fetchQuoteInternal(): Promise<void> {
    if (!this.walletStore || !this.apiTransport || !this.currentQuoteRequest) {
      return
    }

    const userAddress = this.walletStore.safeLensAddress
    const { inputToken, outputToken, inputAmount, slippageBps, chainId, nativeToken } = this.currentQuoteRequest

    if (!inputToken || !outputToken || !inputAmount || inputAmount === "0") {
      return
    }

    try {
      if (inputAmount === "." || inputAmount.endsWith(".")) {
        return
      }

      let inputAmountRaw: bigint
      try {
        inputAmountRaw = inputToken.parseUnits(inputAmount)
      } catch {
        return
      }

      const amountStr = inputAmountRaw.toString().replace(/n$/, "")
      if (amountStr.length > 160) {
        return
      }

      const request: AggregatorQuoteRequest = {
        chainId,
        inputToken,
        outputToken,
        inputAmountRaw,
        inputAmountFormatted: inputAmount,
        slippageBps,
        userAddress,
        nativeToken,
      }

      const rawQuote = await this.apiTransport.fetchQuote(request)
      const normalizedQuote = this.normalizeQuote(rawQuote, request)

      if (normalizedQuote) {
        runInAction(() => {
          this.latestQuote = normalizedQuote
        })

        if (this.eventBus) {
          this.eventBus.publish(new SwapQuoteReceivedEvent(normalizedQuote))
        }
      }
    } catch (error) {
      console.error(`[Kyber] Error fetching quote:`, error)
    }
  }

  private normalizeQuote(rawQuote: unknown, request: AggregatorQuoteRequest): NormalizedSwapQuote | null {
    try {
      const routeResponse = rawQuote as KyberRouteResponse
      const routeSummary = routeResponse.data?.routeSummary
      const routerAddress = routeResponse.data?.routerAddress

      if (!routeSummary?.routeID || !routeSummary.amountOut || !routerAddress) {
        return null
      }

      const quoteId = routeSummary.routeID
      const outputAmountRaw = BigInt(routeSummary.amountOut)

      const exchangeRate =
        request.inputAmountRaw > 0n ? (Number(outputAmountRaw) / Number(request.inputAmountRaw)).toString() : "0"

      const inputTokenPrice = request.inputToken.bestPrice
      const outputTokenPrice = request.outputToken.bestPrice
      const inputAmountUsd = Number(request.inputAmountFormatted) * inputTokenPrice
      const outputAmountFormatted = request.outputToken.formatUnits(outputAmountRaw)
      const outputAmountUsd = Number(outputAmountFormatted) * outputTokenPrice

      const priceImpactBps = this.priceImpactCalculatorParam.calculate(inputAmountUsd, outputAmountUsd)

      const gasLimit = routeSummary.gas ? BigInt(routeSummary.gas) : BigInt(500000)

      const normalizedQuote: NormalizedSwapQuote = {
        aggregatorId: this.id,
        quoteId,
        inputToken: request.inputToken,
        outputToken: request.outputToken,
        inputAmountRaw: request.inputAmountRaw,
        outputAmountRaw,
        inputAmountFormatted: request.inputAmountFormatted,
        outputAmountFormatted,
        exchangeRate,
        priceImpactBps,
        gasEstimate: {
          gasLimit,
        },
        transactionData: null,
        transactionMetadata: {
          routeSummary,
          routerAddress,
          routeID: routeSummary.routeID,
          requestId: routeResponse.requestId,
          targetAddress: routerAddress,
          slippageBps: request.slippageBps,
        },
        timestamp: unixTimestamp(),
      }

      return normalizedQuote
    } catch (error) {
      console.error("Error normalizing Kyber quote:", error)
      return null
    }
  }

  private onQuoteSuccess(_sender: IOrchestratedQuerySender<void>, _data: void): void {
    this.quoteQuery?.stop()
  }

  private onQuoteFailed(_sender: IOrchestratedQuerySender<void>, _error: Error): void {
    this.quoteQuery?.stop()
    console.error("Kyber quote query failed:", _error)
  }

  async fetchQuote(params: AggregatorQuoteRequest): Promise<void> {
    if (!this.apiTransport) {
      throw new Error("Kyber plugin not initialized")
    }

    try {
      const rawQuote = await this.apiTransport.fetchQuote(params)
      const normalizedQuote = this.normalizeQuote(rawQuote, params)

      if (normalizedQuote) {
        runInAction(() => {
          this.latestQuote = normalizedQuote
        })

        if (this.eventBus) {
          this.eventBus.publish(new SwapQuoteReceivedEvent(normalizedQuote))
        }
      }
    } catch (error) {
      console.error("Error fetching Kyber quote:", error)
      throw error
    }
  }

  async buildTransaction(
    quote: NormalizedSwapQuote,
    params: AggregatorTransactionRequest,
  ): Promise<NormalizedSwapTransaction | null> {
    if (!this.apiTransport) {
      return null
    }

    try {
      const quoteId = quote.quoteId
      if (!quoteId) {
        return null
      }

      const rawTransaction = await this.apiTransport.buildTransaction(quoteId, params)

      const parsedTransaction = kyberBuildResponseSchema.safeParse(rawTransaction)
      if (!parsedTransaction.success) {
        console.error("Kyber build response validation failed:", parsedTransaction.error)
        return null
      }

      const kyberBuild = parsedTransaction.data

      const data = kyberBuild.data.data.startsWith("0x")
        ? (kyberBuild.data.data as `0x${string}`)
        : (`0x${kyberBuild.data.data}` as `0x${string}`)

      if (data === "0x") {
        return null
      }

      const gasLimit = kyberBuild.data.gas ? BigInt(kyberBuild.data.gas) : BigInt(500000)

      const normalizedTransaction: NormalizedSwapTransaction = {
        to: kyberBuild.data.routerAddress as `0x${string}`,
        data,
        value: kyberBuild.data.transactionValue ? BigInt(kyberBuild.data.transactionValue) : 0n,
        gasLimit,
      }

      return normalizedTransaction
    } catch (error) {
      console.error("Error building Kyber transaction:", error)
      return null
    }
  }

  supportsDirectTransaction(): boolean {
    return false
  }

  supportsApiTransport(): boolean {
    return true
  }

  supportsOnChainTransport(): boolean {
    return false
  }

  selectCurrentQuote(): void {
    if (this.latestQuote) {
      this.selectedQuote = this.latestQuote
    }
  }

  updateSelectedQuote(quote: NormalizedSwapQuote): void {
    this.selectedQuote = quote
  }

  clearSelection(): void {
    this.selectedQuote = null
  }

  clearQuotes(): void {
    runInAction(() => {
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  private handleQuoteRequested(event: SwapQuoteRequestedEvent): void {
    runInAction(() => {
      this.currentQuoteRequest = event.quoteRequest
    })

    if (this.isEnabled() && this.quoteQuery) {
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleQuoteRequestCleared(_event: SwapQuoteRequestClearedEvent): void {
    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }

    runInAction(() => {
      this.currentQuoteRequest = null
      this.latestQuote = null
      this.selectedQuote = null
    })
  }

  private handleAggregatorEnabled(event: SwapAggregatorEnabledEvent): void {
    if (event.aggregatorId !== this.id) {
      return
    }

    if (this.currentQuoteRequest && this.quoteQuery) {
      if (!this.quoteQuery.isActive) {
        this.quoteQuery.start()
      } else if (!this.quoteQuery.isRunning) {
        this.quoteQuery.forceExecute()
      }
    }
  }

  private handleAggregatorDisabled(event: SwapAggregatorDisabledEvent): void {
    if (event.aggregatorId !== this.id) {
      return
    }

    if (this.quoteQuery?.isActive) {
      this.quoteQuery.stop()
    }
  }

  get displayQuote(): NormalizedSwapQuote | null {
    return this.selectedQuote ?? this.latestQuote
  }

  get hasBetterQuote(): boolean {
    if (!this.selectedQuote || !this.latestQuote) {
      return false
    }

    return this.latestQuote.outputAmountRaw > this.selectedQuote.outputAmountRaw
  }

  async getAllowance(token: TokenStore, spender: Address, userAddress: Address): Promise<bigint> {
    if (!this.config || !this.chainId) {
      return 0n
    }

    try {
      const result = await readContract(this.config, {
        address: token.address.toViemAddress(),
        abi: erc20Abi,
        functionName: "allowance",
        args: [userAddress, spender],
        chainId: this.chainId,
      })

      return result as bigint
    } catch (error) {
      console.error("Error getting allowance:", error)
      return 0n
    }
  }

  async executeAllowanceTransaction(
    quote: NormalizedSwapQuote,
    currentAllowance: bigint,
    userAddress: Address,
    approvalMode: ApprovalMode,
    contractRiskLevel: ContractRiskLevel,
  ): Promise<NormalizedSwapTransaction | null> {
    if (!this.eventBus) {
      throw new Error("Kyber plugin not initialized")
    }

    try {
      this.eventBus.publish(new SwapApprovalStartedEvent(quote))

      const targetAddress = quote.transactionMetadata?.targetAddress as string | undefined
      if (!targetAddress) {
        throw new Error("Target address not found in quote metadata")
      }

      if (currentAllowance >= quote.inputAmountRaw) {
        this.eventBus.publish(
          new SwapApprovalCompletedEvent(quote, "0x0000000000000000000000000000000000000000000000000000000000000000"),
        )
        return null
      }

      const approvalAmount = determineApprovalAmount(quote.inputAmountRaw, approvalMode, contractRiskLevel)

      const spenderAddress = targetAddress.toLowerCase().replace("0x", "").padStart(64, "0")
      const amountHex = approvalAmount.toString(16).padStart(64, "0")
      const data = ("0x095ea7b3" + spenderAddress + amountHex) as `0x${string}`

      const approvalTransaction: NormalizedSwapTransaction = {
        to: quote.inputToken.address.toViemAddress(),
        data: data as `0x${string}`,
        value: 0n,
        gasLimit: BigInt(50000),
      }

      return approvalTransaction
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      if (this.eventBus) {
        this.eventBus.publish(new SwapErrorEvent(quote, errorMessage))
      }
      throw error
    }
  }

  async executeSwapTransaction(
    quote: NormalizedSwapQuote,
    userAddress: Address,
  ): Promise<NormalizedSwapTransaction | null> {
    return this.buildTransaction(quote, {
      quote,
      userAddress,
    })
  }
}
